import { FOOTER_PREMIUM } from "../common/footer";
import { MARKETING_HEADER } from "../common/header";

/**
 * World-Class Lead Nurture Case Study Template
 * Features: Case study format, statistics, testimonial, benefits, compelling CTAs
 */
export const nurtureLead = `
<mjml>
  <mj-head>
    <mj-title>How {{company_name}} Increased Conversions by {{success_percentage}}%</mj-title>
    <mj-preview>See how businesses like yours are winning with chatbots</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .stat-badge { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 8px 20px; border-radius: 25px; font-size: 18px; font-weight: 700; }
      .quote-mark { font-family: Georgia, serif; font-size: 72px; line-height: 0; color: #1939B7; opacity: 0.3; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f1f5f9">
    \${MARKETING_HEADER("Success Stories", "See what's possible")}

    <!-- Introduction -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="24px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Hi {{first_name}},
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          We noticed you've been exploring {{product_name}}. We thought you might like to see how other businesses in your industry are using chatbots to drive real results.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Case Study Header -->
    <mj-section background-color="#ffffff" padding="30px 30px 15px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Case Study
        </mj-text>
        <mj-text font-size="26px" color="#0f172a" font-weight="700">
          {{case_study_company}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Stats Row -->
    <mj-section background-color="#ffffff" padding="20px 30px 30px">
      <mj-column width="33%" padding="10px">
        <mj-text align="center">
          <span style="background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 10px 20px; border-radius: 25px; font-size: 20px; font-weight: 700; display: inline-block;">{{stat_1_value}}</span>
        </mj-text>
        <mj-text font-size="13px" color="#64748b" align="center" padding-top="10px">
          {{stat_1_label}}
        </mj-text>
      </mj-column>
      <mj-column width="33%" padding="10px">
        <mj-text align="center">
          <span style="background: linear-gradient(135deg, #3B82F6, #2563EB); color: white; padding: 10px 20px; border-radius: 25px; font-size: 20px; font-weight: 700; display: inline-block;">{{stat_2_value}}</span>
        </mj-text>
        <mj-text font-size="13px" color="#64748b" align="center" padding-top="10px">
          {{stat_2_label}}
        </mj-text>
      </mj-column>
      <mj-column width="33%" padding="10px">
        <mj-text align="center">
          <span style="background: linear-gradient(135deg, #8B5CF6, #7C3AED); color: white; padding: 10px 20px; border-radius: 25px; font-size: 20px; font-weight: 700; display: inline-block;">{{stat_3_value}}</span>
        </mj-text>
        <mj-text font-size="13px" color="#64748b" align="center" padding-top="10px">
          {{stat_3_label}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Challenge Section -->
    <mj-section background-color="#f8fafc" padding="25px 30px" border-radius="12px">
      <mj-column>
        <mj-text font-size="12px" color="#EF4444" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="10px">
          The Challenge
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          {{challenge_description}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Solution Section -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text font-size="12px" color="#10B981" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="10px">
          The Solution
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          {{solution_description}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Testimonial Quote -->
    <mj-section background-color="#f0f9ff" padding="35px 30px" border-radius="16px" border-left="4px solid #1939B7">
      <mj-column>
        <mj-text font-size="36px" color="#1939B7" line-height="0.5" padding-bottom="15px">
          &#8220;
        </mj-text>
        <mj-text font-size="18px" color="#334155" font-style="italic" line-height="1.7" font-family="Georgia, serif">
          {{testimonial_quote}}
        </mj-text>
        <mj-divider border-color="#1939B7" border-width="2px" width="50px" padding="20px 0 15px" />
        <mj-text font-size="15px" color="#0f172a" font-weight="600">
          {{testimonial_name}}
        </mj-text>
        <mj-text font-size="13px" color="#64748b">
          {{testimonial_title}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Benefits Section -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="12px" color="#1939B7" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Your Potential
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700" padding-bottom="20px">
          What You Could Achieve
        </mj-text>
        <mj-text font-size="14px" color="#64748b" line-height="2.2">
          <span style="background: #1939B7; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#10003;</span> {{benefit_1}}<br>
          <span style="background: #1939B7; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#10003;</span> {{benefit_2}}<br>
          <span style="background: #1939B7; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#10003;</span> {{benefit_3}}<br>
          <span style="background: #1939B7; color: white; width: 24px; height: 24px; border-radius: 50%; display: inline-block; text-align: center; line-height: 24px; font-size: 12px; margin-right: 10px;">&#10003;</span> {{benefit_4}}
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#1939B7" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Ready to See Similar Results?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          Start your free trial and see what's possible for your business.
        </mj-text>
        <mj-button background-color="#2DD4BF" color="#0F172A" href="{{trial_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          Start Free Trial &#8594;
        </mj-button>
        <mj-text font-size="13px" color="rgba(255,255,255,0.7)" align="center" padding-top="15px">
          No credit card required
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Secondary CTA -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="15px" color="#64748b" align="center">
          Want to learn more first? <a href="{{demo_url}}" style="color: #1939B7; text-decoration: none; font-weight: 600;">Schedule a personalized demo</a>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "nurtureLead",
  name: "Lead Nurture - Case Study",
  category: "sequences",
  purpose: "sequence" as const,
  description:
    "A world-class lead nurturing email featuring a customer success story with gradient stat badges, elegant testimonial quote, benefits checklist, and compelling trial CTA.",
  features: [
    "Case study format with stats",
    "Gradient stat badges",
    "Challenge/solution narrative",
    "Elegant testimonial quote",
    "Benefits checklist",
    "No credit card CTA",
  ],
  subject: "How {{company_name}} Increased Conversions by {{success_percentage}}% &#128200;",
  subTitle: "See how businesses like yours are winning",
  mjmlTemplate: nurtureLead,
  tags: ["sequence", "nurture", "lead", "case-study", "conversion"],
};
